import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useTranslation } from '@/hooks/useTranslation';
import logo from '@/assets/images/logo-sidebar.png';

const resetPasswordSchema = z.object({
  verificationCode: z.string().regex(/^\d{6}$/, 'Code must be 6 digits'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [email, setEmail] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // Validate token and get email on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidatingToken(false);
        return;
      }

      try {
        const userEmail = await authApi.validateResetToken(token);
        setEmail(userEmail);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Token inválido ou expirado');
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSendCode = async () => {
    if (!token) {
      setError('Token inválido');
      return;
    }

    try {
      setError(null);
      setIsSendingCode(true);
      await authApi.sendVerificationToken(token);
      setCodeSent(true);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Falha ao enviar código');
    } finally {
      setIsSendingCode(false);
    }
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError(null);
      setIsLoading(true);
      await authApi.resetPasswordWithToken(email, data.verificationCode, data.newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Falha ao redefinir senha');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: '#F6F6F8' }}
      >
        <div 
          className="w-full"
          style={{
            maxWidth: '450px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            padding: '48px 40px',
            boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
          }}
        >
          <div className="text-center">
            <div 
              className="mb-6 p-4 rounded-lg"
              style={{
                backgroundColor: '#FDEDEF',
                border: '1px solid #E5384F'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#E5384F' }}
              >
                {t('auth.invalidResetToken')}
              </p>
            </div>
            <Link
              to="/login"
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: '#F6F6F8' }}
      >
        <div 
          className="w-full"
          style={{
            maxWidth: '450px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            padding: '48px 40px',
            boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
          }}
        >
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src={logo} 
                alt="FeedBatch Logo" 
                style={{ 
                  height: '120px',
                  width: 'auto'
                }}
              />
            </div>
            <div 
              className="mb-6 p-4 rounded-lg"
              style={{
                backgroundColor: '#E8F5E9',
                border: '1px solid #4CAF50'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#2E7D32' }}
              >
                {t('auth.passwordResetSuccess')}
              </p>
            </div>
            <p 
              className="text-sm"
              style={{ color: 'rgba(50, 71, 92, 0.6)' }}
            >
              Redirecionando para o login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#F6F6F8' }}
    >
      <div 
        className="w-full"
        style={{
          maxWidth: '450px',
          backgroundColor: '#FFF',
          borderRadius: '8px',
          padding: '48px 40px',
          boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
        }}
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={logo} 
              alt="FeedBatch Logo" 
              style={{ 
                height: '120px',
                width: 'auto'
              }}
            />
          </div>
          <h2 
            className="text-2xl font-medium mb-2"
            style={{ color: '#22212C', fontWeight: 500 }}
          >
            {t('auth.resetPasswordTitle')}
          </h2>
          <p 
            className="text-sm"
            style={{ color: 'rgba(50, 71, 92, 0.6)' }}
          >
            {t('auth.resetPasswordSubtitle')}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div 
              style={{
                backgroundColor: '#FDEDEF',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #E5384F'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#E5384F' }}
              >
                {error}
              </p>
            </div>
          )}

          {codeSent && (
            <div 
              style={{
                backgroundColor: '#E8F5E9',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #4CAF50'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#2E7D32' }}
              >
                Código de verificação enviado para {email}!
              </p>
            </div>
          )}

          {email && (
            <div 
              style={{
                backgroundColor: '#F6F6F8',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #E0E0E0'
              }}
            >
              <p 
                className="text-xs"
                style={{ color: 'rgba(50, 71, 92, 0.6)' }}
              >
                Redefinindo senha para:
              </p>
              <p 
                className="text-sm font-medium mt-1"
                style={{ color: '#22212C' }}
              >
                {email}
              </p>
            </div>
          )}

          <div>
            <Button 
              type="button" 
              variant="outline"
              className="w-full"
              onClick={handleSendCode}
              isLoading={isSendingCode}
              disabled={!email || isSendingCode || isValidatingToken}
            >
              {codeSent ? 'Reenviar Código' : 'Enviar Código de Verificação'}
            </Button>
            <p 
              className="text-xs mt-2"
              style={{ color: 'rgba(50, 71, 92, 0.6)' }}
            >
              Um código de 6 dígitos será enviado para seu email
            </p>
          </div>

          <Input
            label="Código de Verificação"
            type="text"
            placeholder="000000"
            maxLength={6}
            error={errors.verificationCode?.message}
            {...register('verificationCode')}
            disabled={!codeSent}
          />

          <Input
            label={t('auth.newPassword')}
            type="password"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            {...register('newPassword')}
            disabled={!codeSent}
          />

          <Input
            label={t('auth.confirmNewPassword')}
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
            disabled={!codeSent}
          />

          <Button 
            type="submit" 
            className="w-full" 
            isLoading={isLoading}
            disabled={!codeSent || isLoading}
          >
            {t('auth.resetPasswordButton')}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        </form>

        {/* Links de Privacidade e Termos */}
        <div className="text-center mt-6 flex justify-center gap-3 text-xs" style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Política de Privacidade
          </a>
          <span>•</span>
          <a
            href="/termos-de-servico"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Termos de Serviço
          </a>
        </div>
      </div>
    </div>
  );
}
